/**
 * Cliente HTTP do web.
 *
 * ── Onde ficam os tokens ────────────────────────────────────────────────────
 *
 * O refresh token **não passa pelo JavaScript**: a API o entrega em cookie
 * httpOnly quando o cliente se identifica com `X-Escalae-Client: web`. Um XSS
 * nesta página consegue agir enquanto ela está aberta, mas não consegue
 * exfiltrar o token e reusá-lo depois — que é a diferença entre um incidente
 * contido e uma sessão de 30 dias na mão de terceiros.
 *
 * O access token fica só em memória, nesta variável. Some ao recarregar a
 * página, e o `bootstrap` do store o recupera chamando `/auth/refresh` com o
 * cookie. `localStorage` não é usado para nada de sessão.
 */

import type { ApiErrorBody } from '@nossorachao/types';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

/** Access token em memória — nunca em localStorage nem sessionStorage. */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Array<{ field: string; message: string }>,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Vale retentar: rede caiu ou o servidor está indisponível. */
  get isTransient(): boolean {
    return this.status === 0 || this.status >= 500;
  }

  /** Mapa campo → mensagem, pronto para o formulário. */
  get fieldErrors(): Record<string, string> {
    return Object.fromEntries((this.details ?? []).map((d) => [d.field, d.message]));
  }
}

let onSessionExpired: (() => void) | null = null;

export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler;
}

// -----------------------------------------------------------------------------
// Renovação coalescida
//
// Ao abrir uma tela, várias queries disparam juntas. Se o token expirou, todas
// falham ao mesmo tempo — e se cada uma tentasse renovar, o servidor receberia
// N refresh simultâneos. Como o refresh é rotativo com detecção de reuso, os
// N-1 excedentes seriam lidos como token roubado e derrubariam a sessão.
// -----------------------------------------------------------------------------

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Escalae-Client': 'web' },
        // O cookie do refresh viaja aqui; o corpo vai vazio de propósito.
        credentials: 'include',
        body: '{}',
      });

      if (!response.ok) {
        accessToken = null;
        onSessionExpired?.();
        return null;
      }

      const data = (await response.json()) as { tokens: { accessToken: string } };
      accessToken = data.tokens.accessToken;
      return accessToken;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/** Recupera a sessão ao carregar a página, usando só o cookie. */
export async function restoreSession(): Promise<boolean> {
  return (await refreshAccessToken()) !== null;
}

// -----------------------------------------------------------------------------
// Requisição
// -----------------------------------------------------------------------------

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  skipAuth?: boolean;
  signal?: AbortSignal;
  /** `blob` devolve o corpo cru — usado para baixar arquivo autenticado. */
  responseType?: 'json' | 'blob';
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, skipAuth = false, signal, responseType = 'json' } = options;
  // FormData leva o próprio Content-Type com o boundary do multipart.
  const isForm = body instanceof FormData;

  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value));
  }

  const execute = (token: string | null) =>
    fetch(url.toString(), {
      method,
      headers: {
        ...(isForm ? {} : { 'Content-Type': 'application/json' }),
        Accept: responseType === 'blob' ? '*/*' : 'application/json',
        // Diz à API para usar cookie no refresh em vez de devolver o token.
        'X-Escalae-Client': 'web',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      ...(body !== undefined ? { body: isForm ? body : JSON.stringify(body) } : {}),
      signal,
    });

  let response: Response;

  try {
    response = await execute(skipAuth ? null : accessToken);
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'Sem conexão. Verifique sua internet.');
  }

  if (response.status === 401 && !skipAuth) {
    const renewed = await refreshAccessToken();
    if (renewed) {
      response = await execute(renewed);
    } else {
      onSessionExpired?.();
    }
  }

  if (response.status === 204) return undefined as T;

  if (responseType === 'blob' && response.ok) return (await response.blob()) as T;

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const error = payload as ApiErrorBody | null;
    throw new ApiError(
      response.status,
      error?.code ?? 'UNKNOWN',
      error?.message ?? 'Algo deu errado. Tente novamente.',
      error?.details,
      error?.requestId,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  blob: (path: string) => request<Blob>(path, { method: 'GET', responseType: 'blob' }),
  delete: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE', body }),
};
